/**
 * DraftsTab — onglet "Contenu généré" de WorkDetailPage (Sprint 3).
 *
 * Empty state initial : bouton "Générer drafts" → POST /api/works/{id}/drafts/generate
 * → 1 appel batch Claude 10-20s → grille de `<DraftCard>` apparaît.
 *
 * Cycle de vie de chaque carte (cf. <DraftCard>) :
 * - Approuver = clic immédiat. Si la version porte des `suggested_tags`,
 *   on ouvre un modal "Ajouter ces tags ?" (frottement zéro sur
 *   l'apprentissage du système).
 * - Régénérer = modal hint optionnel.
 * - Rejeter = useConfirm.
 *
 * Vision Tier 1 doit être prête avant la génération : si le BE retourne
 * 409, on affiche un message clair "Analyse Vision en cours, retentez
 * dans quelques secondes".
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import MockupTemplatePicker from '@/components/etsy/MockupTemplatePicker'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useApproveDraft,
  useDrafts,
  useGenerateDrafts,
  useGenerationBatchPolling,
  useRegenerateDraft,
  useRejectDraft,
  useReopenDraft,
} from '@/hooks/useDrafts'
import { useWorkEligibility } from '@/hooks/useEligibility'
import { useMatrixForWork } from '@/hooks/useWorkPodPhysical'
import { workPublicationsKeys } from '@/hooks/useWorkPublications'
import { useWork } from '@/hooks/useWorks'
import { DRAFT_KIND_TO_PLATFORM, DRAFT_KIND_FR, type DraftResponse } from '@/types/drafts'
import { workPodPhysicalApi } from '@/utils/api/work-pod-physical'
import { workPublicationsApi } from '@/utils/api/work-publications'
import type { DraftMockupPreview } from '@/utils/api/drafts'

import DraftApproveDialog from './DraftApproveDialog'
import DraftCard from './DraftCard'
import DraftPreviewModal from './DraftPreviewModal'
import EligibilityPreviewBanner from './EligibilityPreviewBanner'

import './DraftsTab.css'

interface DraftsTabProps {
  workId: number
  /**
   * Quand `true`, l'analyse Vision Tier 1 n'a pas encore tourné — on
   * affiche un message d'attente plutôt que le bouton "Générer drafts".
   */
  visionNotReady: boolean
}

export default function DraftsTab({ workId, visionNotReady }: DraftsTabProps) {
  const draftsQuery = useDrafts(workId)
  const workQuery = useWork(workId)
  const matrixQuery = useMatrixForWork(workId)
  const hasActivePodMatrix = matrixQuery.data !== null && matrixQuery.data !== undefined
  const generateMutation = useGenerateDrafts(workId)
  const approveMutation = useApproveDraft(workId)
  const regenerateMutation = useRegenerateDraft(workId)
  const rejectMutation = useRejectDraft(workId)
  const reopenMutation = useReopenDraft(workId)
  const eligibilityQuery = useWorkEligibility(workId)
  // Mutation publish ad-hoc (`platform` dépend du draft cliqué — un hook
  // dédié par platform serait sur-spécifié ici).
  const qc = useQueryClient()
  const enqueuePublishMutation = useMutation({
    mutationFn: ({
      platform,
      payload,
    }: {
      platform: Parameters<typeof workPublicationsApi.enqueuePublish>[1]
      payload: Parameters<typeof workPublicationsApi.enqueuePublish>[2]
    }) => workPublicationsApi.enqueuePublish(workId, platform, payload),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, vars.platform),
      })
    },
  })
  const noEligibleKinds =
    eligibilityQuery.data !== undefined && eligibilityQuery.data.eligible_kinds.length === 0
  const { confirm } = useConfirm()

  // Refactor 2026-05-15 — génération en background via la queue S5.5.
  // Polling 3s sur le batch_id retourné par l'enqueue ; invalidation
  // drafts automatique en fin de run.
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null)
  const batchStatusQuery = useGenerationBatchPolling({
    batchId: activeBatchId,
    workId,
    onSettled: (status) => {
      setActiveBatchId(null)
      if (status.failed > 0) {
        toast.error(
          `Génération terminée — ${status.done} drafts générés, ${status.failed} en erreur.`,
        )
      } else if (status.done > 0) {
        toast.success(
          `${status.done} draft${status.done > 1 ? 's' : ''} prêt${status.done > 1 ? 's' : ''}.`,
        )
      }
    },
  })
  const isGeneratingHere =
    generateMutation.isPending ||
    (!!activeBatchId &&
      batchStatusQuery.data != null &&
      batchStatusQuery.data.running + batchStatusQuery.data.scheduled > 0)

  // PR2 (2026-05-10) — l'approve devient un flow 3-écrans :
  //   1. <DraftApproveDialog> : choix variante + tags suggérés (skipped
  //      visuellement si 1 seule variante active ET pas de suggested_tags).
  //   2. <MockupTemplatePicker> : sélection 1..8 mockups.
  //   3. <DraftPreviewModal> : previews + bouton "Confirmer & publier"
  //      qui déclenche l'enqueue.
  // L'état `flowState` orchestre la transition d'écran à écran sans
  // perdre la sélection accumulée.
  const [flowState, setFlowState] = useState<
    | { step: 'prep'; draft: DraftResponse }
    | {
        step: 'mockups'
        draft: DraftResponse
        workVariantId: number
        acceptedTags: string[]
      }
    | {
        step: 'preview'
        draft: DraftResponse
        workVariantId: number
        previews: DraftMockupPreview[]
      }
    | null
  >(null)

  const drafts = draftsQuery.data ?? []
  const hasDrafts = drafts.length > 0

  // Quel draft est en cours de mutation ? On bloque l'interaction sur lui.
  const busyDraftId = (() => {
    if (approveMutation.isPending) return approveMutation.variables?.draftId
    if (regenerateMutation.isPending) return regenerateMutation.variables?.draftId
    if (rejectMutation.isPending) return rejectMutation.variables
    if (reopenMutation.isPending) return reopenMutation.variables
    return undefined
  })()

  const handleGenerate = async () => {
    try {
      const batch = await generateMutation.mutateAsync(undefined)
      if (batch.scheduled_count === 0) {
        const reason = batch.skipped[0]?.reason
        if (reason === 'no_missing_kinds') {
          toast.info('Tous les drafts éligibles ont déjà été générés.')
        } else if (reason === 'already_active') {
          toast.info('Une génération est déjà en cours pour cette œuvre.')
        } else {
          toast.info('Aucun draft à générer pour cette œuvre.')
        }
        return
      }
      setActiveBatchId(batch.batch_id)
      toast.info(
        `Génération lancée — ${batch.scheduled_count} job(s) en file. Les drafts apparaîtront sous 30-60s.`,
      )
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Génération impossible : ${detail}`)
    }
  }

  // ── Flow approve (PR2 — 3 écrans : prep → mockups → preview) ────────

  const activeVariants = useMemo(() => {
    const variants = workQuery.data?.variants ?? []
    return variants.filter((v) => v.status === 'active')
  }, [workQuery.data])

  // Bug 2026-05-15 — sans ce filtre, l'user qui clique Approuver sur
  // `etsy_listing_physical` pouvait sélectionner une variante digital
  // dans le wizard (et inversement) → listing publié avec le mauvais
  // medium côté Etsy. Le garde-fou BE rejette désormais ces cas, mais
  // l'UX doit empêcher ce choix en amont.
  // Etsy split `physical / digital` est strict ; pour les autres plate-
  // formes (Pinterest, Instagram, TikTok, Saatchi, Artfinder) tout
  // medium est éligible — la publication est plateforme-specific, pas
  // medium-specific.
  const variantsCompatibleWithDraft = (draft: DraftResponse) => {
    if (draft.kind === 'etsy_listing_physical') {
      return activeVariants.filter((v) => v.medium_kind === 'print' || v.medium_kind === 'original')
    }
    if (draft.kind === 'etsy_listing_digital') {
      return activeVariants.filter((v) => v.medium_kind === 'digital')
    }
    return activeVariants
  }

  const handleApprove = (draft: DraftResponse) => {
    const compatible = variantsCompatibleWithDraft(draft)
    if (activeVariants.length === 0) {
      toast.error("Aucune variante active sur l'œuvre — crée une variante avant d'approuver.")
      return
    }
    if (compatible.length === 0) {
      const needed =
        draft.kind === 'etsy_listing_physical'
          ? 'print ou original'
          : draft.kind === 'etsy_listing_digital'
            ? 'digital'
            : 'compatible'
      toast.error(
        `Aucune variante ${needed} active sur cette œuvre — active une variante ${needed} avant d'approuver ce draft (ou approuve un autre kind).`,
      )
      return
    }
    const suggested = draft.current_version?.suggested_tags ?? []
    // Skip l'écran 1 si 1 seule variante compatible ET pas de tags suggérés.
    if (compatible.length === 1 && (!suggested || suggested.length === 0)) {
      setFlowState({
        step: 'mockups',
        draft,
        workVariantId: compatible[0].id,
        acceptedTags: [],
      })
      return
    }
    setFlowState({ step: 'prep', draft })
  }

  const handlePrepConfirm = (selection: { workVariantId: number; acceptedTags: string[] }) => {
    if (flowState?.step !== 'prep') return
    setFlowState({
      step: 'mockups',
      draft: flowState.draft,
      workVariantId: selection.workVariantId,
      acceptedTags: selection.acceptedTags,
    })
  }

  const handleMockupsConfirm = async (
    mockupTemplateIds: number[],
    pinterestTemplateIds: number[],
  ) => {
    if (flowState?.step !== 'mockups') return
    const { draft, workVariantId, acceptedTags } = flowState
    try {
      const result = await approveMutation.mutateAsync({
        draftId: draft.id,
        payload: {
          mockup_template_ids: mockupTemplateIds,
          work_variant_id: workVariantId,
          accept_suggested_tags: acceptedTags.length > 0 ? acceptedTags : null,
          pinterest_template_ids: pinterestTemplateIds.length > 0 ? pinterestTemplateIds : null,
        },
      })
      // Bascule sur l'écran preview qui affichera les composites
      // rendus. L'ordre final est piloté par le DnD interne du modal
      // (rang Etsy 1..N) — le caller récupère l'ordre courant via
      // le callback `onConfirmPublish(orderedTemplateIds)`.
      setFlowState({
        step: 'preview',
        draft,
        workVariantId,
        previews: result.previews,
      })
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Approbation impossible : ${detail}`)
      setFlowState(null)
    }
  }

  const handleConfirmPublish = async (orderedTemplateIds: number[]) => {
    if (flowState?.step !== 'preview') return
    const { draft, workVariantId } = flowState
    const platform = DRAFT_KIND_TO_PLATFORM[draft.kind]
    try {
      // Phase 8 — refonte M : si l'œuvre a une matrice POD active ET le
      // draft est etsy_listing_physical, route vers publish_matrix (1
      // listing Etsy matriciel multi-variant). Sinon fallback flow
      // single-variant historique (digital, ou print sans matrice).
      if (
        platform === 'etsy' &&
        hasActivePodMatrix &&
        matrixQuery.data &&
        draft.kind === 'etsy_listing_physical'
      ) {
        await workPodPhysicalApi.publish(matrixQuery.data.id, {
          draft_id: draft.id,
          mockup_template_ids: orderedTemplateIds,
        })
        void qc.invalidateQueries({
          queryKey: workPublicationsKeys.forWork(workId, 'etsy'),
        })
      } else {
        await enqueuePublishMutation.mutateAsync({
          platform,
          payload: {
            variant_id: workVariantId,
            draft_id: draft.id,
            mockup_template_ids: orderedTemplateIds,
          },
        })
      }
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} approuvé et mis en file.`)
      setFlowState(null)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Mise en file impossible : ${detail}`)
    }
  }

  const handleRegenerate = async (draft: DraftResponse, hint: string | null) => {
    try {
      await regenerateMutation.mutateAsync({
        draftId: draft.id,
        payload: { extra_hint: hint },
      })
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} régénéré.`)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Régénération impossible : ${detail}`)
    }
  }

  const handleReopen = async (draft: DraftResponse) => {
    try {
      await reopenMutation.mutateAsync(draft.id)
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} réouvert.`)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Réouverture impossible : ${detail}`)
    }
  }

  const handleReject = async (draft: DraftResponse) => {
    const ok = await confirm({
      title: `Rejeter ce draft ${DRAFT_KIND_FR[draft.kind]} ?`,
      message:
        'Le draft sera marqué rejeté. Il alimentera la liste de patterns à éviter pour les futures générations.',
      variant: 'danger',
      confirmText: 'Rejeter',
    })
    if (!ok) return
    try {
      await rejectMutation.mutateAsync(draft.id)
      toast.success('Draft rejeté.')
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Rejet impossible : ${detail}`)
    }
  }

  // ─── États ──────────────────────────────────────────────────────────────

  if (visionNotReady) {
    return (
      <EmptyState
        icon={Sparkles}
        title="Analyse visuelle en cours"
        description="Claude Vision extrait les descripteurs EN qui alimenteront les drafts plateformes. Patiente quelques secondes."
        variant="muted"
      />
    )
  }

  if (draftsQuery.isLoading) {
    return <p className="ui-text-muted">Chargement…</p>
  }

  if (!hasDrafts) {
    return (
      <>
        <EligibilityPreviewBanner workId={workId} />
        <EmptyState
          icon={Sparkles}
          title="Aucun draft généré"
          description="Lance Claude pour produire les drafts éligibles selon les règles configurées dans Réglages."
          action={
            <Button
              variant="primary"
              size="lg"
              onClick={handleGenerate}
              loading={isGeneratingHere}
              disabled={noEligibleKinds || isGeneratingHere}
            >
              <Sparkles size={14} aria-hidden="true" />
              {isGeneratingHere ? 'Génération en cours…' : 'Générer drafts'}
            </Button>
          }
        />
      </>
    )
  }

  return (
    <div className="drafts-tab">
      <EligibilityPreviewBanner workId={workId} />
      <header className="drafts-tab-header">
        <p className="ui-text-muted drafts-tab-summary">
          {drafts.length} draft{drafts.length > 1 ? 's' : ''} générés ·{' '}
          {drafts.filter((d) => d.status === 'approved').length} approuvés
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleGenerate}
          loading={isGeneratingHere}
          disabled={noEligibleKinds || isGeneratingHere}
        >
          <Sparkles size={14} aria-hidden="true" />
          Régénérer manquants
        </Button>
      </header>

      <div className="drafts-tab-grid">
        {drafts.map((draft) => (
          <DraftCard
            key={draft.id}
            draft={draft}
            onApprove={handleApprove}
            onRegenerate={handleRegenerate}
            onReject={handleReject}
            onReopen={handleReopen}
            isBusy={busyDraftId === draft.id}
          />
        ))}
      </div>

      {/* ── Flow approve PR2 — 3 écrans chaînés ──────────────────────────
          1. <DraftApproveDialog> : variante (si plusieurs) + tags suggérés.
          2. <MockupTemplatePicker> : 1..8 mockups.
          3. <DraftPreviewModal> : previews + bouton Confirmer & publier.
       */}
      {flowState?.step === 'prep' ? (
        <DraftApproveDialog
          isOpen
          onClose={() => setFlowState(null)}
          draftKindLabel={DRAFT_KIND_FR[flowState.draft.kind]}
          activeVariants={variantsCompatibleWithDraft(flowState.draft)}
          suggestedTags={flowState.draft.current_version?.suggested_tags ?? []}
          hasActivePodMatrix={
            hasActivePodMatrix && flowState.draft.kind === 'etsy_listing_physical'
          }
          onConfirm={handlePrepConfirm}
        />
      ) : null}

      {flowState?.step === 'mockups' && workQuery.data ? (
        <MockupTemplatePicker
          workId={workId}
          workTitle={workQuery.data.title_fr ?? ''}
          formatSlug={null}
          isOpen
          onClose={() => setFlowState(null)}
          onConfirm={handleMockupsConfirm}
          isSubmitting={approveMutation.isPending}
          confirmLabel="Approuver et rendre"
          submittingLabel="Render…"
          kicker={`Approbation ${DRAFT_KIND_FR[flowState.draft.kind]}`}
          title="Choisir les mockups (1 à 8)"
        />
      ) : null}

      {flowState?.step === 'preview' ? (
        <DraftPreviewModal
          isOpen
          onClose={() => setFlowState(null)}
          draftKindLabel={DRAFT_KIND_FR[flowState.draft.kind]}
          previews={flowState.previews}
          onConfirmPublish={handleConfirmPublish}
          isPublishing={enqueuePublishMutation.isPending}
        />
      ) : null}
    </div>
  )
}
